import { requestIntellicorpBackgroundCheck } from '@/backend/features/background-check/platform-functions/utils/requestIntellicorpBackgroundCheck';
import { requestJnetBackgroundCheck } from '@/backend/features/background-check/platform-functions/utils/requestJnetBackgroundCheck';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { getBackgroundCheckStorage } from '@/backend/features/background-check/utils/getBackgroundCheckStorage';
import { getExtendedAccountStorage } from '@/backend/features/contact-management/utils/getExtendedAccountStorage';
import { hasLowerSystemRole } from '@juvare/contact-management/backend/features/accounts/shared';
import { decryptData } from '@juvare/credentialing/backend/utils';
import { AppContext, AuthError, UserContext } from '@juvare/platform-app/App';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { requireManageMembersAndAnySystemPermission } from '../../../utils';
import { ExtendedAccountData } from '../../contact-management/entities/extended-account/types';
import { BackgroundCheckConfig, BackgroundCheckQuota, BackgroundCheckTest } from '../defaults';
import { BackgroundCheckHistoryColumns, BackgroundCheckHistoryData, backgroundCheckHistoryEntityName } from '../entities';
import { BackgroundCheckStatus, BackgroundCheckType } from '../types';

export interface BackgroundCheckParams {
    accountId: string;
    backgroundCheckType: BackgroundCheckType;
}

export function requestBackgroundCheckScreening(
    context: AppContext<AppState>,
    {
        requestedBy,
        accountId,
        accountData,
        backgroundCheckType,
        config,
        backgroundCheckTestConfig,
        isTestMode,
        backgroundCheck,
        quota,
    }: {
        requestedBy: string;
        accountId: string;
        accountData: ExtendedAccountData;
        backgroundCheckType: BackgroundCheckType;
        config: BackgroundCheckConfig;
        backgroundCheckTestConfig: BackgroundCheckTest;
        isTestMode: boolean;
        backgroundCheck: BackgroundCheckColumns;
        quota: BackgroundCheckQuota;
    },
) {
    if (backgroundCheckType === 'jnet') {
        requestJnetBackgroundCheck({
            requestedBy,
            context,
            config,
            accountData,
            quota,
            backgroundCheckTestConfig,
            accountId,
            backgroundCheck,
            isTestMode,
        });
    } else {
        requestIntellicorpBackgroundCheck({
            context,
            config,
            accountData,
            quota,
            backgroundCheckTestConfig,
            accountId,
            backgroundCheck,
            isTestMode,
            backgroundCheckType,
            requestedBy,
        });
    }
}

export function requestBackgroundCheck(
    context: UserContext<AppState, UserState>,
    { accountId, backgroundCheckType }: BackgroundCheckParams,
): void {
    if (!requireManageMembersAndAnySystemPermission(context.userState, 'request-background-check')) {
        throw new AuthError('Unauthorized');
    }

    const storage = getExtendedAccountStorage(context);
    const accountData = storage.get(accountId).data;
    const backgroundCheckStorage = getBackgroundCheckStorage(context);

    const backgroundCheckData = backgroundCheckStorage.list({
        filter: [
            { column: 'accountId', op: '=', value: accountId },
            { column: 'type', op: '=', value: backgroundCheckType },
        ],
        limit: 1,
    }).rows;

    const backgroundCheck = backgroundCheckData[0].columns;

    if (hasLowerSystemRole(context.userState.systemRole, accountData.systemRole, context.appState.settings.systemRoleMap)) {
        throw new AuthError('You cannot request a background check on this administrator.');
    }

    const config = context.appState.settings.backgroundCheckConfig;
    const backgroundCheckTestConfig = context.appState.settings.backgroundCheckTest;
    const isTestMode = Boolean(
        backgroundCheckTestConfig.useTestData && (backgroundCheckTestConfig.testData || backgroundCheckTestConfig.jnetReturnCode),
    );
    const quota = context.appState.settings.backgroundCheckQuota;

    requestBackgroundCheckScreening(context, {
        requestedBy: context.userState.displayName === context.systemUserId ? 'System' : context.userState.displayName,
        accountId,
        accountData,
        backgroundCheckType,
        config,
        backgroundCheckTestConfig,
        isTestMode,
        backgroundCheck,
        quota,
    });
}

export const getSsn = (context: AppContext<AppState>, accountData: ExtendedAccountData) => {
    if (!accountData.ssn) {
        return;
    }

    const decryptedSsn = decryptData(context, accountData.ssn);

    if (!decryptedSsn?.success) {
        throw new BackgroundCheckError(decryptedSsn.message);
    }

    return decryptedSsn?.data;
};

export function countRequestsByType(context: AppContext<AppState>, type: BackgroundCheckType) {
    const storage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(backgroundCheckHistoryEntityName);
    const result = storage.list({
        filter: [
            { column: 'type', op: '=', value: type },
            { column: 'nextStatus', op: '=', value: 'processingPending' },
            { column: 'isUsingQuota', op: '=', value: true },
        ],
        limit: 0,
    });
    return result.total;
}

export class BackgroundCheckError extends Error {
    backgroundCheckStatus: BackgroundCheckStatus;

    constructor(message: string, backgroundCheckStatus?: BackgroundCheckStatus) {
        super(message);

        this.backgroundCheckStatus = backgroundCheckStatus || 'processingError';
    }
}
